import { ApiError } from "@/lib/api";

/**
 * Convert any thrown value into a user-facing Korean message string.
 *
 * The backend always returns either a plain string in the `error` field of
 * the JSON envelope or an HTTP status. We never want to render a raw
 * Object/Error object to the end user (causes "[object Object]" or stack
 * traces to leak), so callers should pass everything through this helper
 * before binding to JSX.
 */
export function createErrorMessage(err: unknown, fallback?: string): string {
  if (err == null) return fallback ?? "알 수 없는 오류가 발생했습니다.";

  if (err instanceof ApiError) {
    return mapApiErrorMessage(err.status, err.message) || fallback ||
      "요청을 처리하는 중 오류가 발생했습니다.";
  }

  if (err instanceof Error) {
    // TypeError: Failed to fetch / network errors
    if (err.name === "TypeError" || /fetch|network/i.test(err.message)) {
      return "서버와 연결할 수 없습니다. 네트워크 상태를 확인해주세요.";
    }
    return err.message || fallback || "오류가 발생했습니다.";
  }

  if (typeof err === "string") return err;

  // Anything else (e.g. an unexpected object) — never render it raw.
  return fallback ?? "알 수 없는 오류가 발생했습니다.";
}

export function isApiErrorStatus(err: unknown, status: number): boolean {
  return err instanceof ApiError && err.status === status;
}

function mapApiErrorMessage(status: number, raw: string): string {
  // If the backend already returned a Korean message, prefer it.
  if (raw && /[가-힯]/.test(raw)) return raw;

  // Translate well-known English server messages.
  const translations: Record<string, string> = {
    "invalid email or password": "이메일 또는 비밀번호가 올바르지 않습니다.",
    "email already registered": "이미 가입된 이메일입니다.",
    "exam not found": "시험을 찾을 수 없습니다.",
    "exam access already granted": "이미 접근 권한이 있는 시험입니다.",
    "request not found": "신청 내역을 찾을 수 없습니다.",
    "request already reviewed": "이미 처리된 신청입니다.",
    "invalid or expired verification token": "인증 링크가 만료되었거나 잘못되었습니다.",
    "verification token expired": "인증 링크가 만료되었습니다.",
    "invalid refresh token": "세션이 만료되었습니다. 다시 로그인해주세요.",
    "refresh token not found": "세션이 만료되었습니다. 다시 로그인해주세요.",
    "password must be at least 8 characters": "비밀번호는 8자 이상이어야 합니다.",
    "exam access not granted": "이 시험에 대한 접근 권한이 없습니다.",
  };
  const lower = raw?.toLowerCase().trim();
  if (lower && translations[lower]) return translations[lower];

  // HTTP status fallbacks.
  if (status === 401) return "로그인이 필요하거나 세션이 만료되었습니다.";
  if (status === 403) return "권한이 없습니다.";
  if (status === 404) return "요청한 항목을 찾을 수 없습니다.";
  if (status === 409) return "이미 처리된 요청이거나 충돌이 발생했습니다.";
  if (status === 429) return "요청이 너무 잦습니다. 잠시 후 다시 시도해주세요.";
  if (status >= 500) return "서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.";

  return raw || "요청을 처리하는 중 오류가 발생했습니다.";
}
